import { ArrowBackIos as ArrowBackIosIcon, ArrowForwardIos as ArrowForwardIosIcon } from '@mui/icons-material/';
import { Grid } from '@mui/material';
import { RoutalPalette } from '@nx-smartmonkey/ui';
import React, { CSSProperties, ReactNode, useState } from 'react';
import styled from 'styled-components';

import Text from '../../react-components/Text';

interface MenuBarContainerPops {
  isCollapsed: boolean;
}

const MenuBarContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  background: ${RoutalPalette.neutral05};
  position: relative;
  min-width: ${({ isCollapsed }: MenuBarContainerPops) => `${isCollapsed ? `20px` : `260px`};`}  
  transition: min-width 0.25s ease-in-out;
`;

const MenuBarContent = styled.div`
  width: 220px;
  overflow: hidden;
  background: ${RoutalPalette.neutral02n};
  padding: 16px 20px;
  position: absolute;
  height: calc(100% - 32px);
`;

const HiddenBar = styled.button`
  width: 20px;
  background-color: transparent;
  height: 100%;
  border: none;
  cursor: pointer;
  outline: none !important;
  border-left: 1px solid ${RoutalPalette.neutral10};
  position: absolute;
  right: -20px;

  z-index: 451;
  :hover {
    border-color: ${RoutalPalette.primary40};
  }
`;

const HiddenCollapseButton = styled.div`
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${RoutalPalette.oldWhite};
  right: -12px;
  top: 30px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  color: ${RoutalPalette.secondary40};
  border-right: 1px solid ${RoutalPalette.neutral10};
  cursor: pointer;
  z-index: 451;
  :hover {
    color: ${RoutalPalette.neutral05};
    background-color: ${RoutalPalette.primary40};
    box-shadow: 0px 0px 2px 1px ${RoutalPalette.primary40};
    border: none;
  }
`;

export const MenuHeader = styled.div`
  padding-bottom: 16px;
`;

export const MenuSectionContainer = styled.div`
  width: 100%;
  padding: 6px 0 6px 0;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

type MenuSectionProps = {
  id: string;
  text?: ReactNode;
  textId?: string;
  textIntlId?: string;
  textStyle?: CSSProperties;
  active?: boolean;
  icon?: ReactNode;
  indent?: number;
  onClick: (...args: any[]) => any;
};

export const MenuSection = ({
  id,
  text,
  textId,
  textIntlId,
  textStyle,
  active,
  icon,
  indent,
  onClick,
  children,
}: React.PropsWithChildren<MenuSectionProps>) => {
  const [isHover, setHover] = useState(false);
  return (
    <MenuSectionContainer
      id={id}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={indent ? { paddingLeft: `${indent * 12}px` } : {}}
    >
      <Grid container alignItems="center">
        {icon && <span style={{ width: `26px`, height: `100%` }}>{icon}</span>}
        {textIntlId && (
          <Text
            id={textId}
            variant="button"
            style={{
              ...(textStyle ?? {}),
              color: `${active || isHover ? `${RoutalPalette.primary40}` : `${RoutalPalette.secondary40}`}`,
            }}
            intlId={textIntlId}
          />
        )}
        {text && (
          <Text
            id={textId}
            variant="button"
            style={{
              ...(textStyle ?? {}),
              color: `${active || isHover ? `${RoutalPalette.primary40}` : `${RoutalPalette.secondary40}`}`,
            }}
          >
            {text}
          </Text>
        )}
        {children}
      </Grid>
    </MenuSectionContainer>
  );
};

type MenuBarProps = {
  content?: React.ReactElement;
  onCollapseChange?: (...args: any[]) => any;
};

const MenuBar = ({ content, onCollapseChange }: MenuBarProps) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const changeCollapse = (collapsed: boolean) => {
    setIsOpen(collapsed);
    if (onCollapseChange) onCollapseChange(collapsed);
  };

  return (
    <MenuBarContainer isCollapsed={!isOpen}>
      <MenuBarContent>{content}</MenuBarContent>
      <HiddenBar onClick={() => changeCollapse(!isOpen)} />
      <HiddenCollapseButton onClick={() => changeCollapse(!isOpen)}>
        {isOpen ? (
          <ArrowBackIosIcon style={{ color: RoutalPalette.primary00, marginLeft: `4px`, fontSize: `14px` }} />
        ) : (
          <ArrowForwardIosIcon style={{ color: RoutalPalette.primary00, marginLeft: `1px`, fontSize: `14px` }} />
        )}
      </HiddenCollapseButton>
    </MenuBarContainer>
  );
};

export default MenuBar;
